import { CURRENCY_TO_LOCALE_STRING } from "@/common/constants";

const formatCurrency = (
  amount: number,
  currency: string = "IDR",
  locale: string = CURRENCY_TO_LOCALE_STRING.IDR
): string => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    currencyDisplay: "code",
  }).format(amount);
};

const parseCurrency = (currencyString: string | number): number => {
  if (typeof currencyString === "number") {
    return currencyString;
  }

  const cleanedString = currencyString
    .replace(/[^0-9.-]+/g, "")
    .replace(".", "");
  const parsedNumber = parseFloat(cleanedString);

  return isNaN(parsedNumber) ? 0 : parsedNumber;
};

const parseCurrencyFloat = (currencyString: string | number): number => {
  if (typeof currencyString === "number") {
    return currencyString;
  }

  // Remove thousand separators (e.g., periods) but keep the decimal point
  const cleanedString = currencyString.replace(/(?!\.\d+$)[^0-9.-]+/g, ""); // Remove non-numeric characters except for the last period in decimals

  const parsedNumber = parseFloat(cleanedString);

  return isNaN(parsedNumber) ? 0 : parsedNumber;
};

const removeUSD = (input: string): number => {
  if (!input) {
    return 0;
  }

  const cleanedString = input.replace(/USD/g, "").trim();
  const parsedNumber = parseFloat(cleanedString);

  return isNaN(parsedNumber) ? 0 : parsedNumber;
};

const getCurrencySymbol = (currencyCode: string, amount?: number): string => {
  const formatter = new Intl.NumberFormat(
    currencyCode === "USD" ? "en-US" : "id-ID",
    {
      style: "currency",
      currency: currencyCode,
      currencyDisplay: "code",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );

  if (amount !== undefined) {
    return formatter.format(amount);
  } else {
    const parts = formatter.formatToParts(1);
    const symbol = parts.find((part) => part.type === "currency")?.value;
    return symbol || "";
  }
};

const unitsMap = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
];
const tensMap = [
  "",
  "",
  "twenty",
  "thirty",
  "forty",
  "fifty",
  "sixty",
  "seventy",
  "eighty",
  "ninety",
];

const convertToWords = (input: number, currencyName?: string): string => {
  if (input == 0) {
    return "zero";
  }
  if (input < 0) {
    return "minus " + convertToWords(Math.abs(input));
  }
  let words = "";
  if (input / 1e9 >= 1) {
    words += convertToWords(Math.floor(input / 1e9)) + " billion ";
    input %= 1e9;
  }
  if (input / 1e6 >= 1) {
    words += convertToWords(Math.floor(input / 1e6)) + " million ";
    input %= 1e6;
  }
  if (input / 1000 >= 1) {
    words += convertToWords(Math.floor(input / 1000)) + " thousand ";
    input %= 1000;
  }
  if (input / 100 >= 1) {
    words += convertToWords(Math.floor(input / 100)) + " hundred ";
    input %= 100;
  }
  if (Math.floor(input) > 0) {
    if (words != "") {
      words += "and ";
    }
    if (input < 20) {
      words += unitsMap[Math.floor(input)];
    } else {
      words += tensMap[Math.floor(input / 10)];
      if (Math.floor(input) % 10 > 0) {
        words += "-" + unitsMap[Math.floor(input) % 10];
      }
    }
  }

  if (currencyName) {
    words += " " + currencyName;
  }

  if (input % 1 > 0) {
    words += " and ";
    const cents = Math.round((input % 1) * 100);
    if (cents < 20) {
      words += unitsMap[cents];
    } else {
      words += tensMap[Math.floor(cents / 10)];
      if (cents % 10 > 0) {
        words += "-" + unitsMap[cents % 10];
      }
    }
    words += " Cents";
  }
  //words = (words[0]).toUpperCase() + words.substring(1);
  return words;
};

export {
  formatCurrency,
  getCurrencySymbol,
  parseCurrency,
  parseCurrencyFloat,
  removeUSD,
  convertToWords,
};
